<template>
	<div class="export-history-container">
		<!-- 页面标题和筛选区 -->
		<div class="page-header">
			<div class="header-left">
				<h2 class="page-title">导出记录</h2>
				<span class="record-count">共 {{ total }} 条记录</span>
			</div>
			<div class="header-right">
				<!-- 导出类型筛选 -->
				<el-select 
					v-model="exportType" 
					placeholder="导出类型" 
					size="small" 
					class="type-filter"
					@change="handleTypeChange"
				>
					<el-option label="全部类型" value=""></el-option>
					<el-option label="基础导出" value="201"></el-option>
					<el-option label="详细导出" value="202"></el-option>
				</el-select>
				
				<el-button 
					@click="getRecordExportHistory" 
					size="small" 
					icon="el-icon-refresh-left"
					:loading="loading"
				>
					刷新
				</el-button>
			</div>
		</div>

		<!-- 表格区域 -->
		<div class="table-wrapper">
			<el-table 
				:data="tableData" 
				v-loading="loading" 
				border 
				stripe 
				:header-cell-style="headerStyle"
				size="small" 
				class="export-table"
			>
				<el-table-column label="#" type="index" width="60" align="center"></el-table-column>
				
				<el-table-column label="导出类型" width="120" align="center">
					<template v-slot="{ row }">
						<el-tag 
							:type="row.type === 201 ? 'primary' : 'success'" 
							size="small"
							effect="plain"
						>
							{{ row.type === 201 ? '基础导出' : '详细导出' }}
						</el-tag>
					</template>
				</el-table-column>
				
				<el-table-column label="导出时间" prop="created_at" width="180" align="center">
					<template v-slot="{ row }">
						<span class="time-text">{{ formatTime(row.created_at) }}</span>
					</template>
				</el-table-column>
				
				<el-table-column label="操作人" prop="operator.name" width="120" align="center"></el-table-column>
				
				<el-table-column label="文件名" min-width="200">
					<template v-slot="{ row }">
						<div class="file-info">
							<i class="el-icon-document"></i>
							<span class="file-name">{{ getFileName(row.file) }}</span>
						</div>
					</template>
				</el-table-column>
				
				<el-table-column label="文件状态" width="120" align="center">
					<template v-slot="{ row }">
						<el-tag 
							:type="row.file ? 'success' : 'danger'" 
							size="small"
						>
							{{ row.file ? '已生成' : '生成中' }}
						</el-tag>
					</template>
				</el-table-column>
				
				<el-table-column label="操作" width="100" align="center" fixed="right">
					<template v-slot="{ row }">
						<el-button 
							@click="downloadFile(row.file)" 
							type="text" 
							size="small"
							:disabled="!row.file"
							icon="el-icon-download"
						>
							下载
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<!-- 分页区域 -->
		<div class="pagination-wrapper">
			<el-pagination 
				background 
				layout="total, prev, pager, next, jumper" 
				:total="total" 
				:current-page.sync="page"
				:page-size="pageSize" 
				@current-change="getRecordExportHistory"
			>
			</el-pagination>
		</div>
	</div>
</template>

<script>
	import {
		getRecordExportHistoryAPI
	} from '@/api/user-record/record.js'

	export default {
		name: 'UserRecordExportHistory',
		data() {
			return {
				page: 1,
				tableData: [],
				pageSize: 20,
				total: 0,
				loading: false,
				exportType: '', // 导出类型筛选,空字符串表示全部
			}
		},
		computed: {
			headerStyle() {
				return {
					background: '#F5F7FA',
					color: '#606266',
					fontWeight: '600',
					fontSize: '13px'
				}
			}
		},
		created() {
			this.getRecordExportHistory()
		},
		methods: {
			/**
			 * 处理导出类型变化
			 */
			handleTypeChange() {
				this.page = 1
				this.getRecordExportHistory()
			},
			
			/**
			 * 格式化时间显示
			 */
			formatTime(time) {
				if (!time) return '-'
				// 简单格式化: YYYY-MM-DD HH:mm:ss
				return time.replace('T', ' ').substring(0, 19)
			},
			
			/**
			 * 从URL中提取文件名
			 */
			getFileName(fileUrl) {
				if (!fileUrl) return '文件生成中...'
				const splitArr = fileUrl.split('/')
				return splitArr[splitArr.length - 1]
			},
			
			/**
			 * 获取导出历史记录
			 */
			getRecordExportHistory() {
				this.loading = true

				const params = {
					page: this.page
				}
				
				// 如果选择了导出类型,添加到参数中
				if (this.exportType) {
					params.type = parseInt(this.exportType)
				}

				getRecordExportHistoryAPI(params).then(res => {
					this.tableData = res.data
					this.total = res.total
					this.pageSize = res.per_page
					this.loading = false
				}).catch(() => {
					this.loading = false
				})
			},
			
			/**
			 * 下载文件
			 */
			downloadFile(fileUrl) {
				if (!fileUrl) {
					this.$message.warning('文件正在生成中,请稍后再试')
					return
				}

				// 直接打开新窗口下载文件
				// 文件通过 storage link 是公开可访问的
				window.open(fileUrl, '_blank')
				
				this.$message.success('开始下载...')
			}
		}
	}
</script>

<style scoped>
.export-history-container {
	padding: 20px;
	background-color: #fff;
	border-radius: 4px;
}

.page-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
	padding-bottom: 16px;
	border-bottom: 1px solid #EBEEF5;
}

.header-left {
	display: flex;
	align-items: baseline;
	gap: 12px;
}

.page-title {
	margin: 0;
	font-size: 20px;
	font-weight: 600;
	color: #303133;
}

.record-count {
	font-size: 13px;
	color: #909399;
}

.header-right {
	display: flex;
	gap: 12px;
}

.type-filter {
	width: 140px;
}

.table-wrapper {
	margin-bottom: 20px;
}

.export-table {
	width: 100%;
}

.export-table >>> .el-table__header th {
	background-color: #F5F7FA !important;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 8px;
}

.file-info i {
	color: #409EFF;
	font-size: 16px;
}

.file-name {
	font-size: 13px;
	color: #606266;
	word-break: break-all;
}

.time-text {
	font-size: 13px;
	color: #606266;
}

.pagination-wrapper {
	display: flex;
	justify-content: flex-end;
	padding-top: 16px;
	border-top: 1px solid #EBEEF5;
}

/* 响应式适配 */
@media (max-width: 768px) {
	.export-history-container {
		padding: 15px;
	}
	
	.page-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
	}
	
	.header-right {
		width: 100%;
		justify-content: space-between;
	}
	
	.type-filter {
		flex: 1;
	}
}
</style>